import { z } from 'zod'

export const DEFAULT_RECEIPT_ORG_NAME = 'Satyanand Research Institute'

export const DEFAULT_EIGHTY_G_CLAUSE_TEXT =
  "Donations made to 'Satyanand Research Institute' are eligible for the benefit of deduction under Section 80G of the Income Tax Act, 1961. Amount donated: {{amount}}. Authorized by 'Satyanand Research Institute'. This benefit is available under the Old Tax Regime only and does not apply to taxpayers who have opted for the New Tax Regime."

export const DEFAULT_THANK_YOU_MESSAGE =
  'Thank You For Your Generous Contribution'

function emptyToNull(value: string | null | undefined): string | null {
  if (value == null) return null
  const trimmed = value.trim()
  return trimmed === '' ? null : trimmed
}

const optionalNullableString = (max: number, label: string) =>
  z
    .union([
      z
        .string()
        .max(max, { message: `${label} must be at most ${max} characters` }),
      z.null(),
    ])
    .optional()
    .transform(emptyToNull)

const optionalNullableUrl = (label: string) =>
  z
    .union([
      z.string().url({ message: `${label} must be a valid URL` }),
      z.literal(''),
      z.null(),
    ])
    .optional()
    .transform(emptyToNull)

const optionalNullableEmail = z
  .union([
    z.string().email({ message: 'Email must be a valid email address' }),
    z.literal(''),
    z.null(),
  ])
  .optional()
  .transform(emptyToNull)

export const updateReceiptSettingsSchema = z.object({
  org_name: z
    .string({ required_error: 'Legal name is required' })
    .trim()
    .min(1, { message: 'Legal name is required' })
    .max(200, { message: 'Legal name must be at most 200 characters' }),
  org_tagline: z
    .string({ required_error: 'Tagline is required' })
    .trim()
    .min(1, { message: 'Tagline is required' })
    .max(200, { message: 'Tagline must be at most 200 characters' }),
  org_registration_id: optionalNullableString(100, 'NGO / Registration ID'),
  org_address: optionalNullableString(500, 'Address'),
  org_phone: optionalNullableString(40, 'Phone'),
  org_email: optionalNullableEmail,
  org_website: optionalNullableString(200, 'Website'),
  org_pan: optionalNullableString(20, 'Organization PAN'),
  logo_url: optionalNullableUrl('Logo URL'),
  signatory_name: optionalNullableString(200, 'Signatory name'),
  signatory_title: optionalNullableString(300, 'Signatory title'),
  signature_image_url: optionalNullableUrl('Signature image URL'),
  eighty_g_clause_text: optionalNullableString(2000, '80G clause text'),
  thank_you_message: optionalNullableString(300, 'Thank-you message'),
})

export type UpdateReceiptSettingsInput = z.infer<typeof updateReceiptSettingsSchema>

export type ReceiptSettingsResponse = {
  id: string
  org_name: string
  org_tagline: string
  org_registration_id: string | null
  org_address: string | null
  org_phone: string | null
  org_email: string | null
  org_website: string | null
  org_pan: string | null
  logo_url: string | null
  signatory_name: string | null
  signatory_title: string | null
  signature_image_url: string | null
  eighty_g_clause_text: string | null
  thank_you_message: string | null
  updated_at: string
}

export const DEFAULT_RECEIPT_SETTINGS: Omit<
  ReceiptSettingsResponse,
  'id' | 'updated_at'
> = {
  org_name: DEFAULT_RECEIPT_ORG_NAME,
  org_tagline: 'Lets Empower Society',
  org_registration_id: 'Satya',
  org_address: 'Old Rajakhera Road, Shamsabad, Agra, Uttar Pradesh, 283125',
  org_phone: '+91 84450 08485',
  org_email: 'info@satyanandss.org.in',
  org_website: 'www.satyanandss.org.in',
  org_pan: null,
  logo_url: null,
  signatory_name: 'Anupama Sharma',
  signatory_title:
    'Chairman CEC, Satyanand Research Institute, Authorised Signatory',
  signature_image_url: null,
  eighty_g_clause_text: DEFAULT_EIGHTY_G_CLAUSE_TEXT,
  thank_you_message: DEFAULT_THANK_YOU_MESSAGE,
}
